
import axios from 'axios';
import { isGuestMode } from '../guestMode';
import { triggerSessionExpired } from '../../utils/sessionExpiry';

const BASE = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:6089';

const client = axios.create({
  baseURL: BASE,
  timeout: 120_000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// Attach Authorization header from localStorage (if present) on every request.
// Some parts of the code store token under `token` or `access_token`.
client.interceptors.request.use((cfg) => {
  try {
    if (typeof window !== 'undefined') {
      const token = window.localStorage.getItem('token') || window.localStorage.getItem('access_token');
      cfg.headers = cfg.headers || {};
      if (isGuestMode()) {
        delete cfg.headers.Authorization;
        cfg.params = { ...(cfg.params || {}), guest: 'true' };
      } else if (token) {
        cfg.headers.Authorization = `Bearer ${token}`;
      }
      // Marque la requête : authentifiée (token envoyé) et non guest.
      cfg._auth = !!token;
      cfg._guest = isGuestMode();
    }
  } catch (e) {
    // ignore
  }
  if (isGuestMode()) {
    cfg.headers = cfg.headers || {};
    cfg.headers["x-guest-mode"] = "true";
    cfg.params = { ...(cfg.params || {}), guest: "true" };
  }
  return cfg;
});

// Si le backend répond 401 à une requête AUTHENTIFIÉE (token envoyé, hors guest),
// c'est que la session est expirée/invalide → on force la déconnexion propre
// (purge + redirection /login). Sans cela, l'utilisateur reste « connecté »
// côté interface alors que son JWT ne vaut plus rien côté serveur.
client.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      const { config, response } = error || {};
      if (response && response.status === 401 && config && config._auth && !config._guest) {
        triggerSessionExpired();
      }
    } catch (e) {
      /* ignore */
    }
    return Promise.reject(error);
  }
);

// Déduplication/cache court des GET : plusieurs composants montés en même temps
// (header, sidebar, page…) demandaient la même ressource → on partage la
// promesse en vol ET on garde le résultat quelques secondes. Le cache est vidé
// dès qu'une mutation (POST/PUT/PATCH/DELETE) a lieu.
const inflightGets = new Map();
const getCache = new Map();
const GET_CACHE_TTL_MS = 2000;
const invalidateGetCache = () => { getCache.clear(); };

class ApiClient {
  /** @returns {Promise<any>} */
  static async get(endpoint, params = {}) {
    let key;
    try { key = `${endpoint}?${JSON.stringify(params)}`; } catch { key = endpoint; }

    if (inflightGets.has(key)) return inflightGets.get(key);
    const cached = getCache.get(key);
    if (cached && (Date.now() - cached.t) < GET_CACHE_TTL_MS) return cached.data;

    const promise = (async () => {
      try {
        const res = await client.get(endpoint, { params });
        getCache.set(key, { t: Date.now(), data: res.data });
        return res.data;
      } catch (e) {
        if (e?.response?.data && typeof e.response.data === 'object') {
          return { success: false, ...e.response.data };
        }
        return { success: false, message: e?.message || 'Network error', error: e };
      } finally {
        inflightGets.delete(key);
      }
    })();

    inflightGets.set(key, promise);
    return promise;
  }

  static async post(endpoint, payload = {}) {
    try {
      const res = await client.post(endpoint, payload);
      invalidateGetCache();
      return res.data;
    } catch (e) {
      // If the server returned a structured body, use it directly (preserves message, success: false, etc.)
      if (e?.response?.data && typeof e.response.data === 'object') {
        return { success: false, ...e.response.data };
      }
      return { success: false, message: e?.message || 'Network error', error: e };
    }
  }

  static async put(endpoint, payload = {}) {
    try {
      const res = await client.put(endpoint, payload);
      invalidateGetCache();
      return res.data;
    } catch (e) {
      return { success: false, message: e?.message || 'Network error', error: e };
    }
  }

  static async patch(endpoint, payload = {}) {
    try {
      const res = await client.patch(endpoint, payload);
      invalidateGetCache();
      return res.data;
    } catch (e) {
      if (e?.response?.data && typeof e.response.data === 'object') {
        return { success: false, ...e.response.data };
      }
      return { success: false, message: e?.message || 'Network error', error: e };
    }
  }

  static async delete(endpoint, params = {}) {
    try {
      const res = await client.delete(endpoint, { params });
      invalidateGetCache();
      return res.data;
    } catch (e) {
      return { success: false, message: e?.message || 'Network error', error: e };
    }
  }

  // Téléchargement binaire (PDF, etc.) — renvoie { success, blob, headers }.
  static async getBlob(endpoint, params = {}) {
    try {
      const res = await client.get(endpoint, { params, responseType: 'blob' });
      return { success: true, blob: res.data, headers: res.headers };
    } catch (e) {
      return { success: false, message: e?.message || 'Network error', error: e };
    }
  }

  // Téléchargement binaire via POST (ex. génération PDF à la demande).
  static async postBlob(endpoint, payload = {}) {
    try {
      const res = await client.post(endpoint, payload, { responseType: 'blob' });
      return { success: true, blob: res.data, headers: res.headers };
    } catch (e) {
      return { success: false, message: e?.message || 'Network error', error: e };
    }
  }

  // Generic method used in code: ApiClient.allApi(url, value, method)
  static async allApi(endpoint, payload = {}, method = 'post') {
    try {
      const cfg = { url: endpoint, method: method.toLowerCase(), data: payload };
      const res = await client.request(cfg);
      if (method.toLowerCase() !== 'get') invalidateGetCache();
      return res.data;
    } catch (e) {
      if (e?.response?.data && typeof e.response.data === 'object') {
        return { success: false, ...e.response.data };
      }
      return { success: false, message: e?.message || 'Network error', error: e };
    }
  }

  static async postFormData(endpoint, formData) {
    try {
      // Let axios auto-set Content-Type with correct boundary when FormData is passed
      const isFormData = typeof FormData !== 'undefined' && formData instanceof FormData;
      // Ensure multipart header is set so axios will include correct boundary
      const headers = isFormData ? { 'Content-Type': 'multipart/form-data' } : { 'Content-Type': 'application/json' };
      const res = await client.post(endpoint, formData, { headers });
      return res.data;
    } catch (e) {
      console.error('postFormData error:', e?.message, e?.response?.status, e?.response?.data);
      return { success: false, message: e?.message || 'Network error', error: e, status: e?.response?.status, data: e?.response?.data };
    }
  }

  static async postFormFileData(endpoint, formData) {
    return ApiClient.postFormData(endpoint, formData);
  }

  static async multiImageUpload(endpoint, files, _extra = {}, fieldName = 'files') {
    const fd = new FormData();
    const fileList = Array.isArray(files) ? files : [files];
    fileList.forEach((file) => fd.append(fieldName, file));
    return ApiClient.postFormData(endpoint, fd);
  }
}

export default ApiClient;
